import {FC} from 'react'
import {Trans, useTranslation} from '@/translations/i18n'
import {Modal, ModalBody, ModalFooter, ModalTitle} from '@/components/ui/modal-atoms/ModalAtoms'
import {Button} from '@/components/ui/button/Button'
import {InputText} from '@/components/commons/input-text/InputText'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {StyledModalHeader} from '@/components/commons/delete-modal/style'
import {Conversation} from '@/features/conversations/types'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useForm} from 'react-hook-form'
import {useDeleteConversation} from '../../query/useDeleteConversation'
import {useAuthStore} from '@/features/auth/store/store'
import {errorHandler} from '@/utilities/helpers'
import {StyledDeleteConversationModal, StyledDeleteConversationModalOverlay} from './style'

interface DeleteConversationModalProps {
    onClose: () => void
    conversation: Conversation
    onDeleteSuccess?: () => void
}

interface DeleteConfirmationForm {
    confirmation: string
}

export const DeleteConversationModal: FC<DeleteConversationModalProps> = ({onClose, conversation, onDeleteSuccess}) => {
    const {t} = useTranslation()
    const currentUser = useAuthStore(state => state.user)

    const {register, watch} = useForm<DeleteConfirmationForm>({
        defaultValues: {
            confirmation: ''
        }
    })
    const confirmationValue = watch('confirmation')

    const deleteConversationMutation = useDeleteConversation(conversation.patientId, {
        onSuccess: () => {
            onDeleteSuccess?.()
            onClose()
        },
        onError: errorHandler
    })

    // Check if the current user is a member of the conversation
    const isUserMember = currentUser && conversation.members.some(member => member.id === currentUser.id)

    // If user is not a member, show access denied
    if (!isUserMember) {
        return (
            <>
                <StyledDeleteConversationModalOverlay
                    onClick={event => {
                        event.stopPropagation()
                        onClose()
                    }}
                />
                <Modal width="400px" onClick={event => event.stopPropagation()}>
                    <StyledModalHeader style={{border: 'none'}}>
                        <ModalTitle>{t('conversations:delete_conversation_denied_title')}</ModalTitle>
                        <Text size="textMd" palette="neutral" shade="600">
                            {t('conversations:delete_conversation_denied_message')}
                        </Text>
                    </StyledModalHeader>
                    <ModalFooter>
                        <Button variant="secondary" onClick={onClose}>
                            {t('commons:close')}
                        </Button>
                    </ModalFooter>
                </Modal>
            </>
        )
    }

    return (
        <>
            <StyledDeleteConversationModalOverlay
                onClick={event => {
                    event.stopPropagation()
                    onClose()
                }}
            />
            <StyledDeleteConversationModal width="400px" onClick={event => event.stopPropagation()}>
                <StyledModalHeader>
                    <ModalTitle>{t('conversations:delete_conversation_title')}</ModalTitle>
                    <Flexbox direction="column" gap={2}>
                        <Text size="textMd">
                            <Trans
                                i18nKey="conversations:delete_conversation_paragraph_1"
                                values={{title: conversation.title}}
                                components={[
                                    <Text palette="neutral" shade="950" weight="500" size="textMd" render="span" />
                                ]}
                            />
                        </Text>
                        <Text size="textMd">{t('commons:delete_paragraph_2')}</Text>
                    </Flexbox>
                </StyledModalHeader>
                <ModalBody>
                    <Flexbox direction="column" gap={3}>
                        <Text size="textMd">
                            <Trans
                                i18nKey="conversations:type_conversation_title_to_confirm"
                                values={{title: conversation.title}}
                                components={[
                                    <Text palette="neutral" shade="950" weight="500" size="textMd" render="span" />
                                ]}
                            />
                        </Text>
                        <InputText
                            type="text"
                            placeholder={t('conversations:type_conversation_title_placeholder')}
                            {...register('confirmation')}
                        />
                    </Flexbox>
                </ModalBody>
                <ModalFooter>
                    <Button variant="secondary" onClick={onClose}>
                        {t('commons:cancel')}
                    </Button>
                    <Button
                        variant="primaryDanger"
                        disabled={deleteConversationMutation.isPending || !(confirmationValue === conversation.title)}
                        onClick={() => deleteConversationMutation.mutate(conversation.id)}
                    >
                        {t('commons:delete')}
                        {deleteConversationMutation.isPending && <Spinner />}
                    </Button>
                </ModalFooter>
            </StyledDeleteConversationModal>
        </>
    )
}
